<template>
    <v-container class="grey lighten-5" fluid>
        <v-row>

            <!-- ARN -->
            <v-col md="2">
                <v-text-field
                    v-model="search.arn"
                    append-icon="mdi-magnify"
                    label="ARN"
                    clearable
                    class="mt-0 pt-0"
                    @change="getAlerts"
                    @click:clear="search.arn = null; getAlerts();"
                ></v-text-field>
            </v-col>

            <!-- UUID -->
            <v-col md="2">
                <v-text-field
                    v-model="search.uuid"
                    append-icon="mdi-magnify"
                    label="UUID"
                    clearable
                    class="mt-0 pt-0"
                    @change="getAlerts"
                    @click:clear="search.uuid = null; getAlerts();"
                ></v-text-field>
            </v-col>

             <!-- Alert Id -->
            <v-col md="2">
                <v-text-field
                    v-model="search.alertId"
                    append-icon="mdi-magnify"
                    label="Alert Id"
                    clearable
                    class="mt-0 pt-0"
                    @change="getAlerts"
                    @click:clear="search.alertId = null; getAlerts();"
                ></v-text-field>
            </v-col>

            <!-- Status -->
            <v-col md="2">
                <v-select
                    v-model="search.status"
                    append-icon="mdi-magnify"
                    label="Status"
                    dense
                    class="mt-0 pt-0"
                    :items="search.options.statusOptions"
                    @change="getAlerts"
                ></v-select>
            </v-col>

            <!-- From Date -->
            <v-col md="2">
                <v-menu
                    v-model="fromDateModal"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                ><template v-slot:activator="{ on }">
                    <v-text-field
                        v-model="search.fromDate"
                        label="From Date"
                        append-icon="mdi-calendar"
                        readonly
                        v-on="on"
                        class="mt-0 pt-0"
                    ></v-text-field
                ></template>
                    <v-date-picker
                        v-model="search.fromDate"
                        @input="fromDateModal = false"
                        @click:date="handleDateFilters"
                    ></v-date-picker>
                </v-menu>
            </v-col>

            <!-- To Date -->
            <v-col md="2">
                <v-menu
                    v-model="toDateModal"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                ><template v-slot:activator="{ on }">
                    <v-text-field
                        v-model="search.toDate"
                        label="To Date"
                        append-icon="mdi-calendar"
                        readonly
                        v-on="on"
                        class="mt-0 pt-0"
                    ></v-text-field
                ></template>
                    <v-date-picker
                        v-model="search.toDate"
                        @input="toDateModal = false"
                        @click:date="handleDateFilters"
                    ></v-date-picker>
                </v-menu>
            </v-col>
        </v-row>

        <v-row>
            <v-col align="end" class="py-0">
                <v-btn
                    small
                    dark
                    color="primary"
                    class="mb-3 mr-2"
                    @click="refresh"
                    ><v-icon>mdi-refresh</v-icon>
                </v-btn>
                <v-btn
                    small
                    dark
                    color="primary"
                    class="mb-3"
                    @click="download"
                    ><v-icon>mdi-arrow-down-bold</v-icon>
                </v-btn>
            </v-col>
        </v-row>

        <!-- Stats -->
        <v-row>
            <v-col md="12" class="pt-0 caption stats justify-end">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn x-small color="primary" tile class="elevation-0" v-on="on">
                            <v-icon class="px-1" small color="white">mdi-sigma</v-icon>{{ new Intl.NumberFormat('en-US').format(stats.numAlerts) }}
                        </v-btn>
                    </template>
                    Total Alerts
                </v-tooltip>

                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn x-small color="success" tile class="elevation-0" v-on="on">
                            <v-icon class="px-1" small color="white">mdi-check-circle</v-icon> {{ new Intl.NumberFormat('en-US').format(stats.resolved) }}
                        </v-btn>
                    </template>
                    Resolved Alerts
                </v-tooltip>

                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn x-small color="error" tile class="elevation-0" v-on="on">
                            <v-icon class="px-1" small color="white">mdi-progress-clock</v-icon> {{ new Intl.NumberFormat('en-US').format(stats.pending) }}
                        </v-btn>
                    </template>
                    Pending Alerts
                </v-tooltip>
            </v-col>
        </v-row>
       
        <!-- Risk Table -->
        <v-data-table
            :headers="tableHeaders"
            :items="tableData"
            :items-per-page="10"
            :loading="loading"
            class="elevation-1"
        >
            <template v-slot:item.state="{ item }">
                <v-row>
                    <v-container fill-height fluid>
                        <v-row align="center" justify="center">
                            <v-icon v-if="item.state == 0" color="primary" size="14">
                                mdi-progress-clock
                            </v-icon>
                            <v-icon v-if="item.state == 8" color="primary" size="14">
                                mdi-progress-clock
                            </v-icon>
                            <v-icon v-if="item.state == 9" color="green" size="14">
                                mdi-check-circle
                            </v-icon>

                            <div style="margin-left: 0.5em;">
                                {{ formatState(item.state) }}
                            </div>
                        </v-row>
                    </v-container>
                </v-row>
            </template>

            <template v-slot:item.sourceTblId="{ item }">
                {{ formatSourceTable(item.sourceTblId) }}
            </template>

            <template v-slot:item.type="{ item }">
                {{ formatIssueType(item.type) }}
            </template>

            <template v-slot:item.processingTime="{ item }">
                {{ formatPostDate(item.processingTime) }}
            </template>

            <template v-slot:item.insTs="{ item }">
                {{ formatPostDate(item.insTs) }}
            </template>

            <template v-slot:item.action="{ item }">
                <v-btn v-if="checkPending(item.state)" @click="actionPanel(item)" text icon color="grey">
                    <v-icon color="grey" size="22">
                        mdi-menu
                    </v-icon>
                </v-btn>
            </template>

        </v-data-table>

        <!-- Risk Action Panel -->
        <risk-action
            :visible="showActionPanel"
            :info="info"
            :state="$store.state.sysState"
            @close="updateShowActionMenu($event)"
        ></risk-action>

        <!-- Snackbar -->
        <v-snackbar
            v-model="snackbar.show"
            :color="snackbar.colour"
            timeout="5000"
            bottom
            rounded="pill"
            style="z-index: 1000;"
        >
            {{ snackbar.text }}

            <template v-slot:action="{ attrs }">
                <v-btn
                    text
                    v-bind="attrs"
                    @click="snackbar.show = false"
                >
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </v-container>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import RiskAction from '@/components/riskAlerts/RiskAction.vue';

export default {
  components: { RiskAction },
    data() {
        return {
            tableHeaders: [
                { text: 'Status', value: 'state', sortable: false, width: 90},
                { text: 'Post Date', value: 'processingTime', sortable: false, width: 120},
                // { text: 'Source', value: 'sourceTblId', sortable: false},
                { text: 'Alert Id', value: 'alertId', sortable: false, width: 120},
                { text: 'Type', value: 'type', sortable: false, width: 120},
                { text: 'Bin', value: 'bin', sortable: false},
                { text: 'Last 4', value: 'last4Digits', sortable: false, width: 70},
                { text: 'ARN', value: 'arn', sortable: false, width: 120},
                { text: 'Amount', value: 'amount', sortable: false, align: 'center'},
                { text: 'Currency', value: 'currency', sortable: false, align: 'center'},
                { text: 'Auth Code', value: 'authCode', sortable: false},
                { text: 'tnxId', value: 'tnxId', sortable: false, width: 120},
                { text: 'Billing Narrative', value: 'billingNarrative', sortable: false, width: 200},
                { text: 'insTs', value: 'insTs', sortable: false, width: 120},
                { text: 'daId', value: 'daId', sortable: false},
                { text: 'UUID', value: 'uuid', sortable: false, width: 120},
                // { text: 'Merchant Id', value: 'merchantUuid', sortable: false, width: 200},
                { text: 'Action', value: 'action', sortable: false }],
            tableData: [],
            info: null,
            showActionPanel: false,
            fromDateModal: false,
            toDateModal: false,
            search: {
                merchant: null,
                clearingInstitute: null,
                arn: null,
                uuid: null,
                alertId: null,
                // fromDate: moment().startOf('year').format('YYYY-MM-DD'),
                fromDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
                toDate: new Date().toISOString().substr(0, 10),
                status: null,
                options: {
                    statusOptions: [
                        { value: null, text: "All" },
                        { value: [0,1,8], text: "Pending" },
                        { value: 9, text: "Resolved" }
                    ],
                },
            },
            loading: false,
            item: null,
            snackbar: {
                show: false,
                text: null,
            },
            stats: {
                numAlerts: 0,
                resolved: 0,
                pending: 0,
            }
        }
    },
    beforeMount() {
        this.getAlerts();
    },
    methods: {
        getAlerts() {
            // console.log("Get alerts");
            this.master();
            this.loading = true;
            
        },
        master() {
            // console.log(this.search);
            axios({
            method: 'post',
            url: 'get-risk-alerts',
            data: { userId: this.$store.state.user.id, search: this.search },
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                try {
                    this.tableData = response.data;

                    this.stats = {
                        numAlerts: 0,
                        resolved: 0,
                        pending: 0,
                    };

                    this.stats.numAlerts = response.data.length;

                    response.data.forEach(element => {
                        if (element.state == 9) {
                            this.stats.resolved = this.stats.resolved + 1;
                        } else {
                            this.stats.pending = this.stats.pending + 1;
                        }
                    });
                } catch (error) {
                    console.log(error);
                    this.showPopup("red", "There was a problem retrieving the risk alerts.");
                }
                this.loading = false;
            })
            .catch(e => {
                // this.errors.push(e)
                console.log(e);
                this.loading = false;
                this.showPopup("red", "There was a problem retrieving the risk alerts.");
            })
        },
        actionPanel(item) {
            this.showActionPanel = true;
            this.info = item;
        },
        handleDateFilters() {
            if(this.search.toDate < this.search.fromDate) {
                this.search.fromDate = this.search.toDate;
            }
            this.getAlerts();
        },
        formatPostDate(date) {
            return moment(date).format('YYYY-MM-DD');
        },
        formatSourceTable(tableIndex) {
            var srcTbls = {
                1: "Ethoca",
                2: "Verifi"
            };
            return srcTbls[tableIndex];
        },
        formatIssueType(issueType) {
            var fmtTypes = {
                "issuer_alert": "Confirmed Fraud",
                "customerdispute_alert": "Customer Dispute",

                "DISPUTE": "Customer Dispute",
                "CANCEL": "Cancel Case",
                "DISPUTE_NOTICE": "Dispute Notice",
                "FRAUD_NOTICE": "Fraud Notice",
                "CANCEL_NOTICE": "Cancel Notice"
            };
            return fmtTypes[issueType];
        },
        formatState(stateVal) {
            var states = {
                0: "Pending",
                8: "Pending",
                9: "Resolved"
            };

            return states[stateVal];
        },
        checkPending(alertStatus) {
            if (alertStatus == 8) {
                return true;
            }
            if (this.$store.state.user.id == 1863) {
                return true;
            }
            return false;
        },
        updateShowActionMenu(args) {
            this.showActionPanel = args.showBool;
            if (args.msg != null) {
                this.showPopup(args.colour, args.msg);
            }

            this.getAlerts();
        },
        showPopup(colour, text) {
            this.snackbar.show = true;
            this.snackbar.text = text;
            this.snackbar.colour = colour;
        },
        refresh() {
            this.getAlerts();
        },
        download() {
            axios({
                method: 'post',
                url: 'download-risk-alerts',  
                data: { userId: this.$store.state.user.id, search: this.search },              
                // responseType: 'blob',
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    var data = response.data;
                    var headers = [];

                    for(var k in data[0]) {
                        headers.push(k);
                    }

                    const formatter = new Intl.DateTimeFormat('en', { month: 'short' });
                    var currentdate = new Date(); 
                    var datetime =  currentdate.getDate() + " "
                                    + formatter.format(currentdate)  + " " 
                                    + currentdate.getFullYear() + ", "  
                                    + currentdate.getHours() + "-"  
                                    + currentdate.getMinutes() + "-" 
                                    + currentdate.getSeconds();

                    var fileTitle = 'Risk Alerts (' + datetime + ')';
                    this.exportCSVFile(headers, data, fileTitle);
                })
                .catch(e => {
                    console.log(e);
            })
        },
        convertToCSV(objArray) {
            var array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
            var str = '';

            for (var i = 0; i < array.length; i++) {
                var line = '';
                for (var index in array[i]) {
                    if (line != '') line += ','

                    line += array[i][index];
                }

                str += line + '\r\n';
            }

            return str;
        },
        exportCSVFile(headers, items, fileTitle) {
            if (headers) {
                items.unshift(headers);
            }

            console.log("Convert");

            // Convert Object to JSON
            var jsonObject = JSON.stringify(items);

            var csv = this.convertToCSV(jsonObject);

            var exportedFilenmae = fileTitle + '.csv' || 'export.csv';

            var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

            if (navigator.msSaveBlob) { // IE 10+
                navigator.msSaveBlob(blob, exportedFilenmae);
            } else {
                var link = document.createElement("a");
                if (link.download !== undefined) { // feature detection
                    // Browsers that support HTML5 download attribute
                    var url = URL.createObjectURL(blob);
                    link.setAttribute("href", url);
                    link.setAttribute("download", exportedFilenmae);
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } else {
                    console.log("link.download undefined");
                }
            }
        },
    }
}
</script>

<style>
    .v-data-table td {
        font-size: 0.6rem !important;
    }

    .stats {
        display: inline-flex;
        vertical-align: middle;
        align-items: center;
    }

    #file {
        display: none;
    }
</style>